/**
 * batch-54 klant-akkoord-mail (FO-412) - gedeelde types, labels en request-
 * schema's voor de klant-reply op de goedkeurings-mail.
 *
 * Eén bron voor API én backoffice: de API valideert met dezelfde Zod-schema's
 * die het formulier client-side gebruikt, zodat het scherm nooit iets kan sturen
 * dat de API weigert (CLAUDE.md "Validatie & foutmeldingen").
 */
import { z } from 'zod'

// ─── Statussen ───────────────────────────────────────────────────────────────

export const KLANT_AKKOORD_MAIL_STATUSSEN = [
  'NIEUW',
  'VERWERKT_AKKOORD',
  'VERWERKT_AFKEUR',
  'TE_BEOORDELEN',
  'NIET_GEKOPPELD',
  'REEDS_BESLOTEN',
  'GENEGEERD',
  'BEOORDEELD',
  'FOUT',
  'QUARANTAINE',
] as const
export type KlantAkkoordMailStatusKey = (typeof KLANT_AKKOORD_MAIL_STATUSSEN)[number]

export const KLANT_AKKOORD_MAIL_STATUS_LABELS: Record<KlantAkkoordMailStatusKey, string> = {
  NIEUW: 'Nieuw',
  VERWERKT_AKKOORD: 'Akkoord verwerkt',
  VERWERKT_AFKEUR: 'Afkeuring verwerkt',
  TE_BEOORDELEN: 'Te beoordelen',
  NIET_GEKOPPELD: 'Niet gekoppeld',
  REEDS_BESLOTEN: 'Reeds besloten',
  GENEGEERD: 'Genegeerd (automatisch antwoord)',
  BEOORDEELD: 'Beoordeeld',
  FOUT: 'Fout',
  QUARANTAINE: 'Quarantaine',
}

export const KLANT_AKKOORD_MAIL_STATUS_BADGE: Record<
  KlantAkkoordMailStatusKey,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  NIEUW: 'outline',
  VERWERKT_AKKOORD: 'default',
  VERWERKT_AFKEUR: 'destructive',
  TE_BEOORDELEN: 'secondary',
  NIET_GEKOPPELD: 'secondary',
  REEDS_BESLOTEN: 'outline',
  GENEGEERD: 'outline',
  BEOORDEELD: 'default',
  FOUT: 'destructive',
  QUARANTAINE: 'destructive',
}

// ─── AI-oordeel ──────────────────────────────────────────────────────────────

export const KLANT_AKKOORD_MAIL_OORDELEN = ['AKKOORD', 'NIET_AKKOORD', 'ONDUIDELIJK'] as const
export type KlantAkkoordMailOordeelKey = (typeof KLANT_AKKOORD_MAIL_OORDELEN)[number]

export const KLANT_AKKOORD_MAIL_OORDEEL_LABELS: Record<KlantAkkoordMailOordeelKey, string> = {
  AKKOORD: 'Akkoord',
  NIET_AKKOORD: 'Niet akkoord',
  ONDUIDELIJK: 'Onduidelijk',
}

// ─── Review-reden ────────────────────────────────────────────────────────────

export const KLANT_AKKOORD_MAIL_REVIEW_REDENEN = [
  'LAGE_ZEKERHEID',
  'ONDUIDELIJK',
  'VREEMDE_AFZENDER',
  'VEROUDERDE_VERSIE',
  'LEGE_REPLY',
  'CONFLICT',
  'ZWAKKE_MATCH',
  'STATUS_BLOKKEERT',
] as const
export type KlantAkkoordMailReviewRedenKey = (typeof KLANT_AKKOORD_MAIL_REVIEW_REDENEN)[number]

export const KLANT_AKKOORD_MAIL_REVIEW_REDEN_LABELS: Record<
  KlantAkkoordMailReviewRedenKey,
  string
> = {
  LAGE_ZEKERHEID: 'Te weinig zekerheid voor automatisch verwerken',
  ONDUIDELIJK: 'Geen duidelijk besluit in de reactie',
  VREEMDE_AFZENDER: 'Afzender is geen bekend contact van deze klant',
  VEROUDERDE_VERSIE: 'Reactie hoort bij een oudere versie van het overzicht',
  LEGE_REPLY: 'Reactie bevat geen tekst',
  CONFLICT: 'Botst met een eerder besluit',
  ZWAKKE_MATCH: 'Alleen op afzender gekoppeld - controleer het overzicht',
  STATUS_BLOKKEERT: 'Overzicht staat een klant-besluit niet meer toe',
}

// ─── Match-wijze ─────────────────────────────────────────────────────────────

export const KLANT_AKKOORD_MAIL_MATCH_WIJZEN = [
  'HEADER',
  'REFERENTIECODE',
  'AFZENDER',
  'HANDMATIG',
] as const
export type KlantAkkoordMailMatchWijzeKey = (typeof KLANT_AKKOORD_MAIL_MATCH_WIJZEN)[number]

export const KLANT_AKKOORD_MAIL_MATCH_WIJZE_LABELS: Record<
  KlantAkkoordMailMatchWijzeKey,
  string
> = {
  HEADER: 'Antwoord op onze mail',
  REFERENTIECODE: 'Referentiecode in het onderwerp',
  AFZENDER: 'Afzender (zwak signaal)',
  HANDMATIG: 'Handmatig gekoppeld',
}

// ─── Review-besluit ──────────────────────────────────────────────────────────

export const KLANT_AKKOORD_MAIL_BESLUITEN = ['AKKOORD', 'AFKEUR', 'GEEN_BESLUIT'] as const
export type KlantAkkoordMailBesluitKey = (typeof KLANT_AKKOORD_MAIL_BESLUITEN)[number]

export const KLANT_AKKOORD_MAIL_BESLUIT_LABELS: Record<KlantAkkoordMailBesluitKey, string> = {
  AKKOORD: 'Akkoord',
  AFKEUR: 'Afkeuren',
  GEEN_BESLUIT: 'Geen besluit',
}

// ─── DTO ─────────────────────────────────────────────────────────────────────

export interface KlantAkkoordMailIngestDTO {
  id: string
  status: KlantAkkoordMailStatusKey
  statusLabel: string
  mailbox: string
  afzender: string | null
  afzenderAdres: string | null
  onderwerp: string | null
  ontvangenOp: string | null
  referentieCode: string | null

  overzichtId: string | null
  conceptNummer: string | null
  klantNaam: string | null
  entiteitNaam: string | null
  overzichtStatus: string | null
  overzichtTotaal: number | null
  matchWijze: KlantAkkoordMailMatchWijzeKey | null

  oordeel: KlantAkkoordMailOordeelKey | null
  zekerheid: number | null
  citaat: string | null
  /**
   * De uitgepelde tekst van de klant (zonder quote/disclaimer), zodat de
   * beoordelaar de reactie letterlijk naast zich heeft en niet eerst de PDF hoeft
   * te openen. Leeg wanneer de reply geen tekst bevatte.
   */
  replyTekst: string | null

  reviewReden: KlantAkkoordMailReviewRedenKey | null
  reviewToelichting: string | null
  versieAfwijkend: boolean

  besluit: KlantAkkoordMailBesluitKey | null
  besluitReden: string | null
  beoordeeldDoor: string | null
  beoordeeldOp: string | null

  /** Is er een opgeslagen bewijsstuk (mail-render PDF / originele .eml)? */
  heeftPdf: boolean
  heeftEml: boolean

  foutmelding: string | null
  verwerktOp: string | null
  createdAt: string
}

/** Bewijs-soorten die via de same-origin download-proxy op te halen zijn. */
export const KLANT_AKKOORD_MAIL_BEWIJS_SOORTEN = ['pdf', 'eml'] as const
export type KlantAkkoordMailBewijsSoort = (typeof KLANT_AKKOORD_MAIL_BEWIJS_SOORTEN)[number]

// ─── Request-schema's (gedeeld API <-> backoffice) ───────────────────────────

/**
 * Review-besluit op een mail-reactie. AFKEUR vereist een reden (die reden komt
 * in de bestaande afkeur-werkvoorraad terecht); AKKOORD en GEEN_BESLUIT niet.
 * De reden is optioneel-maar-toegestaan bij AKKOORD (interne toelichting).
 */
export const KlantAkkoordMailBesluitSchema = z
  .object({
    besluit: z.enum(KLANT_AKKOORD_MAIL_BESLUITEN),
    reden: z.string().trim().max(2000).optional().nullable(),
  })
  .superRefine((waarde, ctx) => {
    if (waarde.besluit === 'AFKEUR' && (waarde.reden ?? '').trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Een reden is verplicht bij afkeuren.',
      })
    }
  })
export type KlantAkkoordMailBesluitInput = z.infer<typeof KlantAkkoordMailBesluitSchema>

/** Handmatig een niet-gekoppelde mail aan een facturatie-overzicht hangen. */
export const KlantAkkoordMailKoppelSchema = z.object({
  overzichtId: z.string().trim().min(1, 'Kies een facturatie-overzicht.'),
})
export type KlantAkkoordMailKoppelInput = z.infer<typeof KlantAkkoordMailKoppelSchema>

/** Query-filters van de review-lijst (server-side paginering). */
export const KlantAkkoordMailLijstQuerySchema = z.object({
  status: z.enum(KLANT_AKKOORD_MAIL_STATUSSEN).optional(),
  /**
   * Snelfilter: alleen de werkvoorraad. Bewust GEEN `z.coerce.boolean()` -
   * `Boolean('false') === true`, waardoor `?werkvoorraad=false` het filter juist
   * aan zou zetten.
   */
  werkvoorraad: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  zoek: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type KlantAkkoordMailLijstQuery = z.infer<typeof KlantAkkoordMailLijstQuerySchema>

/**
 * Statussen die samen "de werkvoorraad" vormen (zichtbaar, nooit stil).
 *
 * `FOUT` en `QUARANTAINE` horen er bewust bij: dat zijn de twee statussen waar
 * een reactie anders permanent kan blijven hangen zonder dat iemand het ziet -
 * de processor pakt alleen `NIEUW` op. `FOUT` is via de lijst opnieuw te
 * koppelen; `QUARANTAINE` wacht op een ADMIN-vrijgave in het quarantaine-scherm.
 */
export const KLANT_AKKOORD_MAIL_WERKVOORRAAD_STATUSSEN: readonly KlantAkkoordMailStatusKey[] = [
  'TE_BEOORDELEN',
  'NIET_GEKOPPELD',
  'FOUT',
  'QUARANTAINE',
]
